/**
 * Resolves URL context into either the Vault catalog or one authorized Vault.
 *
 * The page owns routing, workspace context, and top-level loading/error states;
 * VaultView owns interaction. This keeps navigation identity separate from the
 * backend authorization that determines what the user can actually see.
 */
import { useEffect, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { useServices } from '@/app-shell/services'
import { Button } from '@/components/ui'
import { useAuth } from '@/subsystems/workspaces'
import { VaultCatalog } from '../components/VaultCatalog'
import { VaultView } from '../components/VaultView'
import type { VaultRecord } from '../types'
import type { VaultTab } from '../components/VaultToolbar'
import { getWorkspaceVaultStatus, listVaults } from '../services/vault-api'
import { listConnectorFeeds } from '../connectors/api'

export function VaultPage() {
  const api = useServices().getClient()
  const {
    accessibleWorkspaces,
    isGlobalAdmin,
    isWorkspaceAdmin,
    selectedOrganizationId,
    selectedWorkspaceId,
    selectWorkspace,
  } = useAuth()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const orgId = selectedOrganizationId ?? ''
  const workspaceStatus = useQuery({
    queryKey: ['workspace-vault-status', orgId, selectedWorkspaceId],
    queryFn: () => getWorkspaceVaultStatus(
      api, orgId, selectedWorkspaceId!,
    ),
    enabled: Boolean(orgId && selectedWorkspaceId),
  })
  const catalog = useQuery({
    queryKey: ['vault-catalog', orgId],
    queryFn: () => listVaults(api, orgId),
    enabled: workspaceStatus.data?.enabled === true,
  })
  const requestedVaultId = params.get('vault')
  const routeOrganizationId = params.get('org')
  const routeWorkspaceId = params.get('ws')
  const selectedEntry = catalog.data?.find(
    entry => entry.vault.vaultId === requestedVaultId,
  )
  const pairedEntries = (catalog.data ?? []).filter(entry => (
    entry.vault.vaultType === 'paired' && (isGlobalAdmin || entry.canAdmin)
  ))
  const catalogFeedQueries = useQueries({
    queries: pairedEntries.map(entry => ({
      queryKey: ['vault-connector-feeds', orgId, entry.vault.vaultId],
      queryFn: () => listConnectorFeeds(api, orgId, entry.vault.vaultId),
      enabled: Boolean(orgId),
    })),
  })
  const inboundConnectorCounts: Record<string, number | undefined> = Object.fromEntries(
    pairedEntries.flatMap((entry, index) => {
      const feeds = catalogFeedQueries[index]?.data?.feeds
      return feeds === undefined
        ? []
        : [[
            entry.vault.vaultId,
            feeds.filter(feed => feed.direction === 'inbound').length,
          ]]
    }),
  )
  const workspaceNames = Object.fromEntries(
    accessibleWorkspaces
      .filter(workspace => workspace.orgId === orgId)
      .map(workspace => [workspace.wsId, workspace.name ?? workspace.wsId]),
  )
  const selectedVault = selectedEntry?.vault
  const pairedWorkspaceId = selectedEntry?.pairing?.workspaceId
  const pairedWorkspace = accessibleWorkspaces.find(
    workspace => workspace.orgId === orgId && workspace.wsId === pairedWorkspaceId,
  )
  const canManagePairedWorkspace = isGlobalAdmin
    || (isWorkspaceAdmin && pairedWorkspaceId === selectedWorkspaceId)
  const canManageCurrentWorkspace = isGlobalAdmin || isWorkspaceAdmin
  const inboundConnectorCount = selectedVault
    ? inboundConnectorCounts[selectedVault.vaultId]
    : undefined
  const selectedTab = (params.get('tab') as VaultTab | null) ?? 'files'

  useEffect(() => {
    if (!requestedVaultId || !routeWorkspaceId || !catalog.data) return
    // cm-coding: The URL is the shareable source for the organization and
    // workspace shown on this page. Wait for that route to settle before
    // choosing its paired Vault; using concurrently changing auth state lets
    // the header and Vault effects each restore the other's stale workspace
    // during a switch.
    if (routeOrganizationId !== orgId) return
    if (selectedEntry?.vault.vaultType !== 'paired') return
    if (selectedEntry.pairing?.workspaceId === routeWorkspaceId) return
    const workspaceEntry = catalog.data.find(entry => (
      entry.vault.vaultType === 'paired'
      && entry.pairing?.workspaceId === routeWorkspaceId
    ))
    // cm-coding: A Vault contains documents for exactly one paired workspace.
    // Keeping the prior workspace's Vault ID after the global selector changes
    // makes the header claim one project while exposing another project's
    // connector controls. Replace that stale route identity atomically; when
    // the selected workspace has no accessible Vault, return to the catalog.
    setParams(current => {
      const next = new URLSearchParams(current)
      if (workspaceEntry) next.set('vault', workspaceEntry.vault.vaultId)
      else next.delete('vault')
      next.delete('tab')
      return next
    }, { replace: true })
  }, [
    catalog.data,
    orgId,
    requestedVaultId,
    routeOrganizationId,
    selectedEntry,
    routeWorkspaceId,
    setParams,
  ])

  const openVault = (vault: VaultRecord) => {
    const entry = catalog.data?.find(value => value.vault.vaultId === vault.vaultId)
    const workspaceId = entry?.pairing?.workspaceId
    if (
      vault.vaultType === 'paired'
      && workspaceId
      && selectedOrganizationId
      && workspaceId !== selectedWorkspaceId
    ) {
      selectWorkspace(selectedOrganizationId, workspaceId)
    }
    setParams(current => {
      const next = new URLSearchParams(current)
      if (vault.vaultType === 'paired' && workspaceId && selectedOrganizationId) {
        next.set('org', selectedOrganizationId)
        next.set('ws', workspaceId)
      }
      next.set('vault', vault.vaultId)
      next.delete('tab')
      return next
    })
  }

  if (!orgId || !selectedWorkspaceId || workspaceStatus.isPending) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Vault…
      </div>
    )
  }

  // cm-coding: Sidebar visibility is not route authorization. Require the
  // selected workspace's Vault status before loading its catalog, and fail
  // closed when Vault is disabled or the status request is rejected.
  if (workspaceStatus.data?.enabled !== true) {
    return <Navigate to={{
      pathname: '/',
      search: new URLSearchParams({
        org: orgId,
        ws: selectedWorkspaceId,
      }).toString(),
    }} replace />
  }

  if (catalog.isPending) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Vault…
      </div>
    )
  }

  return (
    <div className="p-4 sm:px-[26px] sm:py-[22px]">
      {selectedVault ? (
        <div className="space-y-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setParams(current => {
              current.delete('vault')
              current.delete('tab')
              return current
            })}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Vaults
          </Button>
          <VaultView
            key={selectedVault.vaultId}
            orgId={orgId}
            vault={selectedVault}
            initialTab={selectedTab}
            connectorSummary={pairedWorkspaceId && inboundConnectorCount !== undefined
              ? {
                  connectorCount: inboundConnectorCount,
                  workspaceName: pairedWorkspace?.name ?? pairedWorkspaceId,
                  onManage: canManagePairedWorkspace
                    ? () => navigate({
                        pathname: '/workspace-connectors',
                        search: new URLSearchParams({ org: orgId, ws: pairedWorkspaceId }).toString(),
                      })
                    : undefined,
                }
              : undefined}
          />
        </div>
      ) : (
        <VaultCatalog
          entries={catalog.data ?? []}
          orgId={orgId}
          selectedWorkspaceId={selectedWorkspaceId ?? undefined}
          workspaceVaultEnabled={workspaceStatus.data?.enabled}
          workspaceNames={workspaceNames}
          inboundConnectorCounts={inboundConnectorCounts}
          search={search}
          onSearchChange={setSearch}
          onManageConnectors={selectedWorkspaceId && canManageCurrentWorkspace
            ? () => navigate({
                pathname: '/workspace-connectors',
                search: new URLSearchParams({ org: orgId, ws: selectedWorkspaceId }).toString(),
              })
            : undefined}
          onOpen={openVault}
        />
      )}
    </div>
  )
}
